/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useState } from 'react';

/* Import components */
import Api2StoredPlotlyPlot from '@/components/api/Api2StoredPlotlyPlot';
import CodeBlock from '@/components/code/CodeBlock';
import LayoutSectionHeader from '@/components/tabs/layouts/LayoutSectionHeader';
import StaticStoredPlotlyPlot from '@/components/charts/StaticStoredPlotlyPlot';

/* Import configuration */
import { API_STORED_PLOTLY_PLOT_EXAMPLES, EXAMPLE_STATIC_STORED_PLOTLY_FIGURES } from '@/components/tabs/layouts/layoutExamples';

interface StoredPlotlyPlotLayoutsTabProps {
    theme: 'light' | 'dark';
}

function StoredPlotlyPlotLayoutsTab({ theme }: StoredPlotlyPlotLayoutsTabProps) {
    const [selectedApiStoredPlotlyPlotExampleIndex, setSelectedApiStoredPlotlyPlotExampleIndex] =
        useState(0);
    const [selectedStoredPlotlyFigureIndex, setSelectedStoredPlotlyFigureIndex] = useState(0);
    const [staticStoredPlotlyCodeTab, setStaticStoredPlotlyCodeTab] =
        useState<'jsx' | 'data'>('jsx');

    const selectedStoredPlotlyExample =
        EXAMPLE_STATIC_STORED_PLOTLY_FIGURES[selectedStoredPlotlyFigureIndex];
    const selectedApiStoredPlotlyPlotExample =
        API_STORED_PLOTLY_PLOT_EXAMPLES[selectedApiStoredPlotlyPlotExampleIndex];
    const selectedStoredPlotlyFigureCode = `const ${selectedStoredPlotlyExample.codeName} = ${JSON.stringify(
        selectedStoredPlotlyExample.figure,
        null,
        4,
    )};`;

    return (
        <div className="layouts-tab">
            <LayoutSectionHeader
                sectionKey="stored-plotly-plot"
                description="StoredPlotlyPlot renders Plotly JSON figures in the shared chart surface. It can use a ready-made figure directly or load stored Plotly data from API2."
            />

            <section className="layouts-section">
                <h3>StaticStoredPlotlyPlot</h3>
                <p>
                    Renders a ready-made Plotly figure without any API request.
                </p>
                <div className="inline-flex flex-wrap items-center gap-3">
                    <label
                        className="text-sm font-semibold"
                        htmlFor="static-stored-plotly-example"
                    >
                        Stored Plotly example
                    </label>
                    <select
                        id="static-stored-plotly-example"
                        className="select select-bordered w-auto"
                        value={selectedStoredPlotlyFigureIndex}
                        onChange={(event) => {
                            setSelectedStoredPlotlyFigureIndex(Number(event.target.value));
                        }}
                    >
                        {EXAMPLE_STATIC_STORED_PLOTLY_FIGURES.map((example, index) => (
                            <option key={example.codeName} value={index}>
                                {example.label}
                            </option>
                        ))}
                    </select>
                </div>
                <h4>Code example</h4>
                <div className="layouts-code-tabs">
                    <div className="tabs tabs-lift">
                        <button
                            type="button"
                            className={`tab ${staticStoredPlotlyCodeTab === 'jsx' ? 'tab-active' : ''}`}
                            onClick={() => {
                                setStaticStoredPlotlyCodeTab('jsx');
                            }}
                        >JSX</button>
                        <button
                            type="button"
                            className={`tab ${staticStoredPlotlyCodeTab === 'data' ? 'tab-active' : ''}`}
                            onClick={() => {
                                setStaticStoredPlotlyCodeTab('data');
                            }}
                        >Data</button>
                    </div>
                    <CodeBlock
                        className="layouts-code--tabbed"
                        code={staticStoredPlotlyCodeTab === 'data'
                            ? selectedStoredPlotlyFigureCode
                            : `<StaticStoredPlotlyPlot
    figure={${selectedStoredPlotlyExample.codeName}}
/>`}
                        language={staticStoredPlotlyCodeTab === 'data' ? 'typescript' : 'jsx'}
                        roundedTop={false}
                        theme={theme}
                    />
                </div>
                <h4>Rendered preview</h4>
                <section className="dashboard-panel dashboard-panel--chart layouts-demo-panel layouts-demo-panel--plot">
                    <StaticStoredPlotlyPlot
                        figure={selectedStoredPlotlyExample.figure}
                        fillParent
                    />
                </section>
            </section>

            <section className="layouts-section">
                <h3>Api2StoredPlotlyPlot</h3>
                <p>
                    Loads stored Plotly JSON from API2 and renders it with the shared figure presenter.
                </p>
                <div className="inline-flex flex-wrap items-center gap-3">
                    <label
                        className="text-sm font-semibold"
                        htmlFor="api-stored-plotly-plot-example"
                    >
                        API Plotly example
                    </label>
                    <select
                        id="api-stored-plotly-plot-example"
                        className="select select-bordered w-auto"
                        value={selectedApiStoredPlotlyPlotExampleIndex}
                        onChange={(event) => {
                            setSelectedApiStoredPlotlyPlotExampleIndex(Number(event.target.value));
                        }}
                    >
                        {API_STORED_PLOTLY_PLOT_EXAMPLES.map((example, index) => (
                            <option key={example.label} value={index}>
                                {example.label}
                            </option>
                        ))}
                    </select>
                </div>
                <h4>Code example</h4>
                <div className="layouts-code-tabs">
                    <div className="tabs tabs-lift">
                        <button type="button" className="tab tab-active">JSX</button>
                    </div>
                    <CodeBlock
                        className="layouts-code--tabbed"
                        roundedTop={false}
                        code={selectedApiStoredPlotlyPlotExample.code}
                        theme={theme}
                    />
                </div>
                <h4>Rendered preview</h4>
                <section className="dashboard-panel dashboard-panel--chart layouts-demo-panel layouts-demo-panel--plot">
                    <Api2StoredPlotlyPlot
                        reference={selectedApiStoredPlotlyPlotExample.props.reference}
                        urn={selectedApiStoredPlotlyPlotExample.props.urn}
                        range={selectedApiStoredPlotlyPlotExample.props.range}
                        title={selectedApiStoredPlotlyPlotExample.props.title}
                    />
                </section>
            </section>
        </div>
    );
}

export default StoredPlotlyPlotLayoutsTab;
